import { apiRequest } from "@common/api";
import { timeAgo } from "@jkast90/kast-common/format";
import type { GamePlay } from "./playtime";

// The games-and-players feed from play-api's /dashboard.

export interface GameSaves {
  /** The ROM's base name. */
  game: string;
  lastSave: string;
  hasBatterySave: boolean;
  states: number;
}

/**
 * What a player chose as their picture, kept as a reference: one of their
 * pictures in the family's pool, or a game's cover (by play key).
 */
export type Picture = { kind: "pool"; id: string; url: string } | { kind: "cover"; gameId: string };

export interface UserSummary {
  sub: string;
  email: string | null;
  /** The account's display name from Cognito, when set. */
  name: string | null;
  lastSave: string | null;
  /** Games with cloud saves. */
  games: GameSaves[];
  /** Play time per game, most recent first. */
  played: GamePlay[];
  secondsPlayed: number;
  lastPlayed: string | null;
  /** Null until they choose: then the most played game's cover. */
  picture: Picture | null;
}

export interface GamePlayer {
  sub: string;
  email: string | null;
  name: string | null;
  seconds: number;
  lastPlayed: string;
}

export interface GameSummary {
  game: string;
  name: string;
  system: string;
  seconds: number;
  lastPlayed: string;
  players: GamePlayer[];
}

export interface Feed {
  players: UserSummary[];
  games: GameSummary[];
}

export async function getDashboard(): Promise<Feed> {
  return (await apiRequest("/dashboard")).json();
}

/** How a player is shown: their display name, else the part of their email before the @, else their account id. */
export function playerName(player: { sub: string; email: string | null; name?: string | null }): string {
  return player.name || (player.email ?? `Account ${player.sub.slice(0, 8)}`).split("@")[0];
}

/** The games with the most time on them across every player, longest first. */
/** The game whose cover is a player's picture: their choice, else their most played. */
export function pictureGame(player: UserSummary): string | null {
  const cover = player.picture?.kind === "cover" ? player.picture.gameId : null;
  const chosen = cover && player.played.find((g) => g.game === cover);
  const most = [...player.played].sort((a, b) => b.seconds - a.seconds)[0];
  return (chosen || most)?.name ?? null;
}

/**
 * A player's picture, as the player card and profile page show it: their pool
 * picture, else the cover of the game they chose, else of their most played.
 * Null when there is none (no feed, unknown player, nothing played).
 */
export function playerPicture(
  feed: Feed | undefined,
  sub: string | null,
): { kind: "pool"; url: string } | { kind: "cover"; game: string } | null {
  const player = sub ? feed?.players.find((p) => p.sub === sub) : undefined;
  if (!player) return null;
  if (player.picture?.kind === "pool") return { kind: "pool", url: player.picture.url };
  const game = pictureGame(player);
  return game ? { kind: "cover", game } : null;
}

export function topGames(feed: Feed, count: number): GameSummary[] {
  return feed.games
    .filter((game) => game.seconds > 0)
    .sort((a, b) => b.seconds - a.seconds)
    .slice(0, count);
}

/** The players with the most time played, longest first. */
export function topPlayers(feed: Feed, count: number): UserSummary[] {
  return feed.players
    .filter((player) => player.secondsPlayed > 0)
    .sort((a, b) => b.secondsPlayed - a.secondsPlayed)
    .slice(0, count);
}

/** A player's most recent activity, play or save. */
export function lastActive(user: UserSummary): string | null {
  return [user.lastPlayed, user.lastSave].filter(Boolean).sort().pop() ?? null;
}

/** "4m ago", "2h ago", "3d ago", or the date past a week (timeAgo, narrow). */
export const ago = (iso: string) => timeAgo(iso, Date.now(), { maxUnit: "day", style: "narrow", numeric: "always" });
